import { arrayWithValue, arrayWithoutValue } from '@/utils/iteration';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useLoadingBar } from 'react-top-loading-bar';
import { useDebounce } from 'react-use';

const loadingReasonsAtom = atom<string[]>([]);

export const useTopLoading = (isLoading: boolean, uniqueId: string) => {
    const setValue = useSetAtom(loadingReasonsAtom);
    useEffect(() => {
        if (isLoading) {
            setValue((prev) => arrayWithValue(prev, uniqueId));
        } else {
            setValue((prev) => arrayWithoutValue(prev, uniqueId));
        }
    }, [isLoading, uniqueId, setValue]);
};

export function ReactQueryTopLoadingBar() {
    const { start, complete } = useLoadingBar();
    const [isLoadingBarActive, setIsLoadingBarActive] = useState(false);
    const loadingReasons = useAtomValue(loadingReasonsAtom);

    const numQuerying = useIsFetching();
    const numMutation = useIsMutating();
    const isFetching = numQuerying + numMutation > 0;

    useDebounce(
        () => {
            const shouldBeActive = isFetching || loadingReasons.length > 0;
            if (shouldBeActive && !isLoadingBarActive) {
                setIsLoadingBarActive(true);
                start('continuous');
            }

            if (!shouldBeActive && isLoadingBarActive) {
                setIsLoadingBarActive(false);
                complete();
            }
        },
        100,
        [isFetching, loadingReasons],
    );

    return null;
}

export const useTopLoadingBar = ({ loading }: { loading: boolean }) => {
    const { start, complete } = useLoadingBar();

    useEffect(() => {
        if (loading) {
            start('continuous');
        } else {
            complete();
        }
    }, [loading, complete, start]);
};
